import type { ApiSession, LeadSummary } from "@fmc/contracts";
import { withTenantHeader } from "./tenant-context";
import { notifySessionExpired } from "./session-expiry";

interface ProblemDetails {
  title?: string;
  detail?: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await apiResponse(path, init);
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export async function apiBlob(path: string, init: RequestInit = {}): Promise<Blob> {
  return (await apiResponse(path, init)).blob();
}

async function apiResponse(path: string, init: RequestInit): Promise<Response> {
  const headers = withTenantHeader(init.headers);
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  const response = await fetch(path, {
    ...init,
    credentials: "include",
    headers,
  });
  if (!response.ok) {
    if (response.status === 401) notifySessionExpired();
    const problem = (await response.json().catch(() => ({}))) as ProblemDetails;
    throw new ApiError(response.status, problem.detail ?? problem.title ?? "Request failed");
  }
  return response;
}

export const getSession = () => api<ApiSession>("/api/session");
export const getLeads = (query = "") =>
  api<{ items: LeadSummary[]; nextCursor: string | null }>(`/api/leads${query}`);

export async function eraseLead(leadId: string, password: string): Promise<void> {
  const reauth = await api<{ token: string }>("/api/reauthenticate", {
    method: "POST",
    body: JSON.stringify({ password, action: "lead.erase" }),
  });
  await api(`/api/leads/${encodeURIComponent(leadId)}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${reauth.token}` },
  });
}
